import { useCallback, useEffect, useRef, useState } from "react";
import { getChecklistStatusNotes } from "../API";
import { ACTION_LABEL, ACTIONS, STATUS_LABEL } from "../utils/checklistStatus";
import "../assets/styles/checklist-notes-history.scss";

// Bucket-based status-badge colour, reusing the dot colours already
// established at project-checklist.scss:65-83 rather than inventing a new
// palette (TRD §3.10). Bucketed the same way the board/utils/checklistStatus
// bucket status: New/Open -> "open", Submitted/In Review -> "inReview",
// Approved -> "approved". The transition is always written out in text too
// (STATUS_LABEL), never colour alone (AC-F5.3).
const STATUS_BUCKET_CLASS = {
  0: "status-open",
  1: "status-open",
  2: "status-inReview",
  3: "status-inReview",
  4: "status-approved",
};

const statusBadgeClass = (status) => STATUS_BUCKET_CLASS[status] || "status-open";

const actorDisplayName = (entry) => {
  const user = entry?.created_by_user;
  // The API returns { id, name } (the User entity has a single `name`
  // column); first_name/last_name is kept as a fallback shape.
  const name = (
    user?.name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ")
  ).trim();
  return name || "Unknown user";
};

const formatTimestamp = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const entryModifierClass = (action) => {
  if (action === ACTIONS.RECALL) return "checklist-notes-history-entry--recall";
  if (action === ACTIONS.RETURN) return "checklist-notes-history-entry--return";
  return "";
};

/**
 * The status-change audit trail for one checklist (TRD §3.10). Rendered
 * inside a collapsible right-side offcanvas panel on UnitJob.js, toggled
 * from the footer action bar.
 *
 * `hideHeading` suppresses the internal "Status History" <h3> when the
 * host (the offcanvas header) already provides the visible title; the
 * section then labels itself via aria-label so it stays a named landmark.
 *
 * Self-fetching: fetches on mount and re-fetches whenever
 * `projectCheckListId` or `refreshToken` changes, so the caller only has to
 * bump `refreshToken` after a status change that keeps the user on the page
 * (AC-F5.8).
 *
 * Renders the server's own ordering verbatim, oldest-first (AC-F5.2) — the
 * server already sorts `created_at ASC, id ASC` (TRD §3.7); this component
 * never re-sorts client-side.
 *
 * Security: note text is rendered as plain React children (JSX default
 * escaping) — `dangerouslySetInnerHTML` is never used anywhere in this
 * component tree (AC-F5.9, R9).
 */
const ChecklistNotesHistory = ({
  projectCheckListId,
  refreshToken,
  hideHeading = false,
}) => {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Guards a fetch that resolves after a newer one has already started
  // (e.g. a fast refreshToken bump right after mount) from clobbering
  // fresher state with a stale response.
  const requestIdRef = useRef(0);

  const fetchNotes = useCallback(async () => {
    if (!projectCheckListId) {
      setEntries([]);
      setIsLoading(false);
      setLoadError(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setLoadError(false);
    try {
      const res = await getChecklistStatusNotes(projectCheckListId);
      if (requestId !== requestIdRef.current) return;
      setEntries(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to fetch checklist status notes:", error);
      setLoadError(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [projectCheckListId]);

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNotes, refreshToken]);

  return (
    <section
      className={`checklist-notes-history${
        hideHeading ? " checklist-notes-history--panel" : ""
      }`}
      aria-labelledby={
        hideHeading ? undefined : "checklist-notes-history-heading"
      }
      aria-label={hideHeading ? "Status History" : undefined}
    >
      {!hideHeading && (
        <h3
          id="checklist-notes-history-heading"
          className="checklist-notes-history-heading"
        >
          Status History
        </h3>
      )}

      {isLoading ? (
        <p className="checklist-notes-history-status" aria-live="polite">
          Loading notes…
        </p>
      ) : loadError ? (
        <div className="checklist-notes-history-error" role="alert">
          <span>Could not load notes.</span>
          <button
            type="button"
            className="btn btn-secondary checklist-notes-history-retry"
            onClick={fetchNotes}
          >
            Retry
          </button>
        </div>
      ) : entries.length === 0 ? (
        <p className="checklist-notes-history-status">
          No notes recorded yet.
        </p>
      ) : (
        <ol className="checklist-notes-history-list">
          {entries.map((entry) => {
            const actor = actorDisplayName(entry);
            const verb = ACTION_LABEL[entry.action] || entry.action;
            return (
              <li
                key={entry.id}
                className={`checklist-notes-history-entry ${entryModifierClass(
                  entry.action
                )}`.trim()}
              >
                <div className="checklist-notes-history-entry-header">
                  <span className="checklist-notes-history-entry-summary">
                    <strong>{actor}</strong> {verb} this checklist
                  </span>
                  <time
                    className="checklist-notes-history-entry-timestamp"
                    dateTime={entry.created_at}
                  >
                    {formatTimestamp(entry.created_at)}
                  </time>
                </div>
                <div className="checklist-notes-history-entry-transition">
                  <span
                    className={`checklist-status-badge ${statusBadgeClass(
                      entry.from_status
                    )}`}
                  >
                    <span className="checklist-status-dot" aria-hidden="true" />
                    {STATUS_LABEL[entry.from_status] ?? "Unknown"}
                  </span>
                  <span
                    className="checklist-notes-history-entry-arrow"
                    aria-hidden="true"
                  >
                    →
                  </span>
                  <span
                    className={`checklist-status-badge ${statusBadgeClass(
                      entry.to_status
                    )}`}
                  >
                    <span className="checklist-status-dot" aria-hidden="true" />
                    {STATUS_LABEL[entry.to_status] ?? "Unknown"}
                  </span>
                </div>
                {entry.note ? (
                  <p className="checklist-notes-history-entry-note">
                    {entry.note}
                  </p>
                ) : (
                  <p className="checklist-notes-history-entry-note checklist-notes-history-entry-note--empty">
                    No note provided
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

export default ChecklistNotesHistory;
